import { useQuery } from '@tanstack/react-query';

import { EmptyState, ErrorState, LoadingState } from '@/components/ui/state-views';
import { StatusBadge } from '@/components/ui/status-badge';
import { getProviderExternalCalendar } from '@/config/api-helpers';

function Toggle({ enabled }: { enabled: boolean }) {
  return (
    <span className='relative inline-flex h-5 w-9 shrink-0 cursor-not-allowed rounded-full bg-neutral-100'>
      <span
        className={`absolute left-0.5 top-[50%] size-4 translate-y-[-50%] rounded-full bg-white shadow ${
          enabled ? 'translate-x-4' : ''
        }`}
      />
    </span>
  );
}

const ROW = 'grid grid-cols-2 divide-x divide-neutral-100 border-t border-neutral-100';
const HL = 'rounded bg-red-100 px-1';

export function ExternalCalendarTab({ providerId }: { providerId: number }) {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['provider-external-calendar', providerId],
    queryFn: () => getProviderExternalCalendar(providerId),
  });

  if (error)
    return (
      <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='External calendar is unavailable' />
    );
  if (isLoading) return <LoadingState message='Loading external calendar…' />;
  if (!data?.nourish && !data?.healthie) return <EmptyState message='No external calendar connected.' />;

  const n = data.nourish;
  const h = data.healthie;

  const emailMismatch = n != null && h != null && n.email !== h.email;
  const pullMismatch = n != null && h != null && n.pullEventsEnabled !== h.pullFromCalendar;
  const pushMismatch = n != null && h != null && n.pushEventsEnabled !== h.addToCalendar;

  // Build merged calendar list — unified order, dash where one side doesn't have it
  const healthieCalIds = new Set(h?.pulledInCalendars ?? []);

  const mergedCalendars = (() => {
    const seen = new Set<string>();
    const result: {
      calendarId: string;
      nourish: { name: string; isEnabled: boolean } | null;
      healthie: { name: string; enabled: boolean } | null;
      mismatch: boolean;
    }[] = [];

    // Add all Nourish subcalendars first
    for (const sub of n?.subcalendars ?? []) {
      seen.add(sub.calendarId);
      const hCal = h?.calendarList.find((c) => c.calendarId === sub.calendarId);
      const hEnabled = healthieCalIds.has(sub.calendarId);
      const mismatch = h != null && (hCal == null || sub.isEnabled !== hEnabled);
      result.push({
        calendarId: sub.calendarId,
        nourish: { name: sub.name, isEnabled: sub.isEnabled },
        healthie: hCal ? { name: hCal.name, enabled: hEnabled } : null,
        mismatch,
      });
    }

    // Add Healthie-only calendars — always a mismatch when Nourish data exists
    for (const cal of h?.calendarList ?? []) {
      if (seen.has(cal.calendarId)) continue;
      const hEnabled = healthieCalIds.has(cal.calendarId);
      result.push({
        calendarId: cal.calendarId,
        nourish: null,
        healthie: { name: cal.name, enabled: hEnabled },
        mismatch: n != null,
      });
    }

    return result;
  })();

  return (
    <div className='text-sm'>
      <div className='grid grid-cols-2 divide-x divide-neutral-100'>
        <div className='p-5'>
          <p className='text-xs font-medium uppercase tracking-wide text-neutral-400'>Nourish</p>
        </div>
        <div className='p-5'>
          <p className='text-xs font-medium uppercase tracking-wide text-neutral-400'>Healthie</p>
        </div>
      </div>

      {/* Email */}
      <div className={ROW}>
        <div className='p-5'>
          <p className='text-neutral-500'>Email</p>
          <p className={`mt-0.5 text-neutral-900 ${emailMismatch ? HL : ''}`}>{n?.email ?? '\u2014'}</p>
        </div>
        <div className='p-5'>
          <p className='text-neutral-500'>Email</p>
          <p className={`mt-0.5 text-neutral-900 ${emailMismatch ? HL : ''}`}>{h?.email ?? '\u2014'}</p>
        </div>
      </div>

      {/* Connection status */}
      <div className={ROW}>
        <div className='flex items-center gap-2 p-5'>
          <p className='text-neutral-500'>Status</p>
          {n ? (
            <StatusBadge
              status={n.status}
              colors={{ SUCCESS: 'bg-green-50 text-green-700', FAILED: 'bg-red-50 text-red-700' }}
              label={n.status === 'SUCCESS' ? 'Connected' : 'Failed'}
            />
          ) : (
            <span className='text-neutral-500'>{'\u2014'}</span>
          )}
        </div>
        <div className='flex items-center gap-2 p-5'>
          <p className='text-neutral-500'>Status</p>
          {h ? (
            <StatusBadge
              status={h.hasRefreshToken ? 'connected' : 'expired'}
              colors={{ connected: 'bg-green-50 text-green-700', expired: 'bg-red-50 text-red-700' }}
              label={h.hasRefreshToken ? 'Connected' : 'Token expired'}
            />
          ) : (
            <span className='text-neutral-500'>{'\u2014'}</span>
          )}
        </div>
      </div>

      {/* Pull events */}
      <div className={ROW}>
        <div className='flex items-center gap-3 p-5'>
          <Toggle enabled={n?.pullEventsEnabled ?? false} />
          <span className={`text-neutral-500 ${pullMismatch ? HL : ''}`}>Pull events</span>
        </div>
        <div className='flex items-center gap-3 p-5'>
          <Toggle enabled={h?.pullFromCalendar ?? false} />
          <span className={`text-neutral-500 ${pullMismatch ? HL : ''}`}>Pull from calendar</span>
        </div>
      </div>

      {/* Push events */}
      <div className={ROW}>
        <div className='flex items-center gap-3 p-5'>
          <Toggle enabled={n?.pushEventsEnabled ?? false} />
          <span className={`text-neutral-500 ${pushMismatch ? HL : ''}`}>Push events</span>
        </div>
        <div className='flex items-center gap-3 p-5'>
          <Toggle enabled={h?.addToCalendar ?? false} />
          <span className={`text-neutral-500 ${pushMismatch ? HL : ''}`}>Add to calendar</span>
        </div>
      </div>

      {/* Subscribed calendars */}
      <div className='border-t border-neutral-100 p-5'>
        <p className='mb-3 text-neutral-500'>Subscribed calendars</p>
        {mergedCalendars.length === 0 ? (
          <span className='text-neutral-400'>{'\u2014'}</span>
        ) : (
          <table className='w-full text-left'>
            <thead>
              <tr className='text-xs font-medium uppercase tracking-wide text-neutral-400'>
                <th className='pb-2 font-medium'>Nourish</th>
                <th className='pb-2 font-medium'>Healthie</th>
              </tr>
            </thead>
            <tbody>
              {mergedCalendars.map((cal) => (
                <tr key={cal.calendarId}>
                  <td className='py-1.5 pr-4'>
                    {cal.nourish ? (
                      <span className='inline-flex items-center gap-2'>
                        <Toggle enabled={cal.nourish.isEnabled} />
                        <span className={`text-neutral-500 ${cal.mismatch ? HL : ''}`}>{cal.nourish.name}</span>
                      </span>
                    ) : (
                      <span className={`text-neutral-400 ${cal.mismatch ? HL : ''}`}>{'\u2014'}</span>
                    )}
                  </td>
                  <td className='py-1.5'>
                    {cal.healthie ? (
                      <span className='inline-flex items-center gap-2'>
                        <Toggle enabled={cal.healthie.enabled} />
                        <span className={`text-neutral-500 ${cal.mismatch ? HL : ''}`}>{cal.healthie.name}</span>
                      </span>
                    ) : (
                      <span className={`text-neutral-400 ${cal.mismatch ? HL : ''}`}>{'\u2014'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
